import React, { useRef, useState, useEffect } from 'react';

/**
 * Drop-in replacement for native <select> with green hover/selection colors.
 * Props mirror a native select: value, onChange, options, placeholder, style, minWidth, disabled, error.
 *
 * options: [{ value: string, label: string }]
 * disabled: grays trigger, closes dropdown
 * error:    shows red border instead of green/gray
 */
const AppSelect = ({ value, onChange, options = [], placeholder = '', style = {}, minWidth, className, disabled, error }) => {
    const [open, setOpen] = useState(false);
    const [hoveredValue, setHoveredValue] = useState(null);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const selectedLabel = options.find(o => o.value === value)?.label || placeholder;

    const borderColor = error ? '#ef4444' : open ? '#1a7d3e' : '#d1fae5';

    const base = {
        position: 'relative',
        display: 'inline-block',
        minWidth: minWidth || style.minWidth || '140px',
        userSelect: 'none',
        opacity: disabled ? 0.6 : 1,
    };

    const trigger = {
        height: '36px',
        fontSize: '13px',
        color: value ? '#374151' : '#9ca3af',
        backgroundColor: disabled ? '#f9fafb' : '#fff',
        border: `1.5px solid ${borderColor}`,
        borderRadius: '8px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        transition: 'border-color 0.15s',
        boxShadow: open && !error ? '0 0 0 3px rgba(26,125,62,0.12)' : 'none',
        outline: 'none',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...style,
        minWidth: undefined,
        padding: '0 32px 0 10px',
    };

    const arrow = {
        position: 'absolute',
        right: '10px',
        top: '50%',
        transform: open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
        pointerEvents: 'none',
        color: open ? '#1a7d3e' : '#9ca3af',
        transition: 'transform 0.15s, color 0.15s',
        display: 'flex',
        alignItems: 'center',
    };

    const menu = {
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0,
        minWidth: '100%',
        backgroundColor: '#fff',
        border: '1.5px solid #d1fae5',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(26,125,62,0.10)',
        zIndex: 9999,
        overflow: 'hidden',
        padding: '4px',
    };

    const getOptionStyle = (optValue) => ({
        padding: '7px 10px',
        fontSize: '13px',
        cursor: 'pointer',
        borderRadius: '5px',
        color: optValue === value ? '#1a7d3e' : hoveredValue === optValue ? '#1a7d3e' : '#374151',
        backgroundColor: optValue === value ? '#e8f5e9' : hoveredValue === optValue ? '#f0fdf4' : 'transparent',
        fontWeight: optValue === value ? '600' : '400',
        transition: 'background-color 0.1s, color 0.1s',
        whiteSpace: 'nowrap',
    });

    const handleSelect = (optValue) => {
        onChange({ target: { value: optValue } });
        setOpen(false);
    };

    return (
        <div ref={ref} style={base} className={className}>
            <div
                style={trigger}
                onClick={() => { if (!disabled) setOpen(v => !v); }}
            >
                {selectedLabel}
            </div>

            <span style={arrow}>
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </span>

            {open && !disabled && (
                <div style={menu}>
                    {options.map(opt => (
                        <div
                            key={opt.value}
                            style={getOptionStyle(opt.value)}
                            onMouseEnter={() => setHoveredValue(opt.value)}
                            onMouseLeave={() => setHoveredValue(null)}
                            onMouseDown={() => handleSelect(opt.value)}
                        >
                            {opt.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AppSelect;
